/**
 * Allys Area Map Component
 * 
 * Interactive map card showing the area location with
 * fullscreen modal support.
 */

'use client';

import { useState, useCallback } from 'react';
import { MapPin, ArrowsOut, X } from '@phosphor-icons/react';
import type { AreaMapProps, AreaPageMessages } from '@routeroyal/theme-core';

export interface AllysAreaMapProps extends AreaMapProps {
  /**
   * Translation messages
   */
  messages?: AreaPageMessages;
}

export function AllysAreaMap({
  area,
  allowFullscreen = true,
  zoom: _zoom = 13,
  messages,
  className = '',
}: AllysAreaMapProps) {
  const [isFullScreen, setIsFullScreen] = useState(false);

  // Generate OpenStreetMap embed URL
  const mapUrl = `https://www.openstreetmap.org/export/embed.html?bbox=${area.longitude - 0.02}%2C${area.latitude - 0.015}%2C${area.longitude + 0.02}%2C${area.latitude + 0.015}&layer=mapnik&marker=${area.latitude}%2C${area.longitude}`;
  
  // Generate Google Maps URL for external link
  const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${area.latitude},${area.longitude}`;

  const handleClose = useCallback(() => {
    setIsFullScreen(false);
  }, []);

  return (
    <>
      {/* Inline Card Version */}
      <div 
        className={`bg-white p-2 rounded-[2rem] shadow-[0_4px_20px_-4px_rgba(0,0,0,0.08)] hover:shadow-[0_20px_40px_-12px_rgba(0,0,0,0.12)] border border-gray-100/80 flex flex-col relative overflow-hidden group h-full hover:-translate-y-1 transition-all duration-500 ${className}`}
      >
        <div className="relative w-full h-full min-h-[400px] rounded-[1.8rem] overflow-hidden bg-gray-50 isolate">
          {/* Map Iframe */}
          <iframe
            src={mapUrl}
            className="absolute inset-0 w-full h-full border-0"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title={`Map of ${area.name}, ${area.region}`}
          />

          {/* Overlay Content */}
          <div className="absolute bottom-0 left-0 right-0 p-8 z-10 pointer-events-none">
            <div className="flex justify-between items-end">
              <div className="pointer-events-auto bg-white/90 backdrop-blur-sm p-4 rounded-2xl shadow-sm border border-white/50">
                <div className="flex items-center gap-2 mb-1 text-[var(--color-text-muted)]">
                  <MapPin size={14} className="text-[var(--color-primary)]" weight="fill" />
                  <span 
                    className="text-xs font-mono tracking-widest uppercase font-inter"
                  >
                    {messages?.locationMap || 'Location Map'}
                  </span>
                </div>
                <h3 className="font-serif text-lg text-[var(--color-text)] leading-none">
                  {area.name}
                </h3>
              </div>
            </div>
          </div>

          {/* Fullscreen Button */}
          {allowFullscreen && (
            <button 
              onClick={() => setIsFullScreen(true)}
              className="absolute top-4 right-4 z-20 w-10 h-10 bg-white rounded-full flex items-center justify-center text-[var(--color-text)] shadow-lg hover:scale-110 transition-transform cursor-pointer"
              title={messages?.viewFullscreen || 'View Fullscreen'}
              aria-label="View map fullscreen"
            >
              <ArrowsOut size={18} weight="bold" />
            </button>
          )}

          {/* Open in Google Maps Link */}
          <a
            href={googleMapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="absolute top-4 left-4 z-20 px-3 py-2 bg-white rounded-full flex items-center gap-2 text-[var(--color-text)] text-xs font-medium shadow-lg hover:bg-gray-50 transition-colors"
          >
            <MapPin size={14} weight="fill" className="text-[var(--color-primary)]" />
            Open in Maps
          </a>
        </div>
      </div>

      {/* Fullscreen Modal */}
      {isFullScreen && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 md:p-8 animate-fadeIn"
          role="dialog"
          aria-modal="true"
          aria-label={`Fullscreen map of ${area.name}`}
        >
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/90 backdrop-blur-sm" 
            onClick={handleClose}
          />

          {/* Modal Container */}
          <div 
            className="relative w-full h-full bg-white rounded-3xl overflow-hidden shadow-2xl flex flex-col animate-scaleIn"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Map */}
            <iframe
              src={mapUrl}
              className="absolute inset-0 w-full h-full border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title={`Fullscreen map of ${area.name}, ${area.region}`}
            />

            {/* Modal Controls */}
            <div className="absolute top-6 right-6 z-[1000] flex gap-4">
              <button 
                onClick={handleClose}
                className="w-12 h-12 bg-white rounded-full flex items-center justify-center text-[var(--color-text)] shadow-xl hover:bg-[var(--color-primary)] hover:text-white transition-colors"
                aria-label="Close fullscreen map"
              >
                <X size={24} weight="bold" />
              </button>
            </div>

            {/* Modal Info Panel */}
            <div className="absolute top-6 left-6 z-[1000] max-w-sm opacity-0 md:opacity-100 pointer-events-none md:pointer-events-auto transition-opacity">
              <div className="bg-white/95 backdrop-blur-md p-6 rounded-3xl shadow-xl border border-white/20">
                <h2 className="font-serif text-3xl text-[var(--color-text)] mb-2">
                  {area.name}
                </h2>
                <p 
                  className="text-sm text-gray-500 font-light leading-relaxed mb-4 font-inter"
                >
                  {area.description.split('.')[0]}.
                </p>
                
                <div 
                  className="grid grid-cols-2 gap-4 text-xs font-mono text-gray-400 border-t border-gray-100 pt-4 font-inter"
                >
                  <div>
                    <span className="block uppercase tracking-widest mb-1 text-[var(--color-primary)]">
                      {messages?.latitude || 'Lat'}
                    </span>
                    <span className="text-[var(--color-text)] font-bold">
                      {area.latitude.toFixed(4)}
                    </span>
                  </div>
                  <div>
                    <span className="block uppercase tracking-widest mb-1 text-[var(--color-primary)]">
                      {messages?.longitude || 'Long'}
                    </span>
                    <span className="text-[var(--color-text)] font-bold">
                      {area.longitude.toFixed(4)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Animation Styles */}
      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes scaleIn {
          from { 
            opacity: 0;
            transform: scale(0.95);
          }
          to { 
            opacity: 1;
            transform: scale(1);
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
        
        .animate-scaleIn {
          animation: scaleIn 0.3s ease-out;
        }
      `}</style>
    </>
  );
}
